import { Col, Form, Row, Select } from "antd";
import dayjs from "dayjs";
import { FC } from "react";
import { CustomSelect } from "~/components/common";
import { useGetClients } from "~/services/client/client.queries";
import { useBooking } from "~/store";
import { capitalize } from "~/utils";
import { ClientStatus, JobStatus, ServiceType } from "~/utils/constants";

interface BookingFiltersProps {
  showFilters: boolean;
}

export const BookingFilters: FC<BookingFiltersProps> = ({ showFilters }) => {
  const { filters, setFilters } = useBooking();

  if (!showFilters) return null;

  const handleDateRangeChange = (dateRange: string) => {
    let startDate: string | undefined;
    let endDate: string | undefined;

    const today = dayjs();

    switch (dateRange) {
      case "today": {
        startDate = today.startOf("day").format("YYYY-MM-DD");
        endDate = today.endOf("day").format("YYYY-MM-DD");
        setFilters({ startDate, endDate, timeFilter: undefined, page: 1 });
        break;
      }
      case "thisWeek": {
        const startOfWeek = dayjs().startOf("week");
        startDate = startOfWeek.format("YYYY-MM-DD");
        endDate = startOfWeek.add(6, "day").format("YYYY-MM-DD");
        setFilters({ startDate, endDate, timeFilter: undefined, page: 1 });
        break;
      }
      case "upcoming": {
        setFilters({
          timeFilter: "upcoming",
          startDate: undefined,
          endDate: undefined,
          page: 1,
        });
        break;
      }
      case "past": {
        setFilters({
          timeFilter: "past",
          startDate: undefined,
          endDate: undefined,
          page: 1,
        });
        break;
      }
      default: {
        setFilters({ startDate: undefined, endDate: undefined, timeFilter: undefined, page: 1 });
        break;
      }
    }
  };

  const mapServiceType = (type: string): ServiceType | undefined => {
    switch (type) {
      case "collection-delivery":
        return ServiceType.COLLECTION;
      case "delivery":
        return ServiceType.DELIVERY;
      case "site-service":
        return ServiceType.ON_SITE_SERVICE;
      case "warehouse":
        return ServiceType.WAREHOUSE_SERVICE;
      case "fabrication":
        return ServiceType.FABRICATION_SERVICE;
      default:
        return undefined;
    }
  };

  return (
    <Form layout="vertical" className="border-t pt-4 mt-4">
      <Row gutter={16}>
        <Col span={24} md={6}>
          <Form.Item label="Status">
            <Select
              value={filters?.bookingStatus || ""}
              onChange={(bookingStatus: string) => {
                const validStatus = bookingStatus === "" ? undefined : bookingStatus;
                setFilters({ bookingStatus: validStatus, page: 1 });
              }}
              options={[
                { value: "", label: "All Statuses" },
                { value: "DRAFT", label: "Draft" },
                { value: "ESTIMATED", label: "Estimated" },
                { value: "SCHEDULED", label: "Scheduled" },
                { value: "IN-PROGRESS", label: "In Progress" },
                { value: "COMPLETED", label: "Completed" },
              ]}
            />
          </Form.Item>
        </Col>

        <Col span={24} md={6}>
          <Form.Item label="Date Range">
            <Select
              value={
                filters?.startDate &&
                filters?.endDate &&
                filters.startDate === filters.endDate
                  ? "today"
                  : filters?.startDate && !filters?.endDate
                  ? "upcoming"
                  : !filters?.startDate && filters?.endDate
                  ? "past"
                  : filters?.startDate && filters?.endDate
                  ? "thisWeek"
                  : "all"
              }
              onChange={handleDateRangeChange}
              options={[
                { value: "all", label: "All Dates" },
                { value: "today", label: "Today" },
                { value: "thisWeek", label: "This Week" },
                { value: "upcoming", label: "Upcoming" },
                { value: "past", label: "Past" },
              ]}
            />
          </Form.Item>
        </Col>

        <Col span={24} md={6}>
          <SelectClient />
        </Col>

        <Col span={24} md={6}>
          <Form.Item label="Service Type">
            <Select
              value={
                filters?.serviceType === ServiceType.COLLECTION
                  ? "collection-delivery"
                  : filters?.serviceType === ServiceType.DELIVERY
                  ? "delivery"
                  : filters?.serviceType === ServiceType.ON_SITE_SERVICE
                  ? "site-service"
                  : filters?.serviceType === ServiceType.WAREHOUSE_SERVICE
                  ? "warehouse"
                  : filters?.serviceType === ServiceType.FABRICATION_SERVICE
                  ? "fabrication"
                  : "all"
              }
              onChange={(type: string) =>
                setFilters({ serviceType: mapServiceType(type), page: 1 })
              }
              options={[
                { value: "all", label: "All Types" },
                {
                  value: "collection-delivery",
                  label: "Collection & Delivery",
                },
                { value: "delivery", label: "Delivery Only" },
                { value: "site-service", label: "Site Service" },
                { value: "warehouse", label: "Warehouse" },
                { value: "fabrication", label: "Fabrication" },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

const SelectClient = () => {
  const { filters, setFilters } = useBooking();

  const { data: clients, isLoading: clientsLoading } = useGetClients({
    params: { status: ClientStatus.ACTIVE, sortByName: "ASC", getAll: true },
  });

  return (
    <Form.Item label="Client">
      <CustomSelect
        showSearch
        placeholder="Select a client"
        optionFilterProp="children"
        loading={clientsLoading}
        allowClear
        filterOption={(input, option) =>
          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
        }
        options={[
          { value: "", label: "All Clients" },
          ...(clients?.items.map((client) => ({
            value: client.id,
            label:
              client.companyName ||
              [client.firstName, client.lastName]
                .filter(Boolean)
                .join(" ")
                .trim() ||
              "Unnamed Client",
          })) || [])
        ]}
        value={filters?.clientId || ""}
        onChange={(clientId: string) =>
          setFilters({
            clientId: clientId === "" ? undefined : clientId,
            page: 1,
          })
        }
      />
    </Form.Item>
  );
};
